import { useEffect, useRef, useState } from "react";
import type { ChartSeries } from "./series.js";

// Export the current chart as PNG (rasterized from the live SVG) or CSV
// (range-filtered series points aligned by timestamp). CHARTS_REDESIGN_PLAN.md §8.

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportCsv(series: ChartSeries[], filename: string) {
  // Union of timestamps across all series, sorted ascending.
  const times = Array.from(new Set(series.flatMap((s) => s.points.map((p) => p.x)))).sort((a, b) => a - b);
  const valueAt = (s: ChartSeries, x: number) => s.points.find((p) => p.x === x)?.y;
  const header = ["date", ...series.map((s) => s.name)];
  const rows = times.map((x) => [
    new Date(x).toISOString(),
    ...series.map((s) => {
      const v = valueAt(s, x);
      return v == null ? "" : String(v);
    }),
  ]);
  const escape = (cell: string) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell);
  const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  download(new Blob([csv], { type: "text/csv" }), `${filename}.csv`);
}

function exportPng(svg: SVGSVGElement, filename: string) {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const vb = svg.viewBox.baseVal;
  const w = vb.width || svg.clientWidth || 720;
  const h = vb.height || svg.clientHeight || 280;
  clone.setAttribute("width", String(w));
  clone.setAttribute("height", String(h));
  const xml = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = () => {
    const scale = window.devicePixelRatio || 2;
    const canvas = document.createElement("canvas");
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // Detect dark mode so exported text/lines stay visible on a solid bg.
      const dark = document.documentElement.classList.contains("dark");
      ctx.fillStyle = dark ? "#0a0a0a" : "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (blob) download(blob, `${filename}.png`);
      });
    }
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

export function ExportMenu({
  svgRef,
  series,
  filename,
}: {
  svgRef: React.RefObject<SVGSVGElement | null>;
  series: ChartSeries[];
  filename: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const itemClass =
    "block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="min-h-[44px] rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:min-h-0 sm:py-1.5"
      >
        Export ▾
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <button
            type="button"
            className={itemClass}
            onClick={() => {
              if (svgRef.current) exportPng(svgRef.current, filename);
              setOpen(false);
            }}
          >
            PNG image
          </button>
          <button
            type="button"
            className={itemClass}
            onClick={() => {
              exportCsv(series, filename);
              setOpen(false);
            }}
          >
            CSV data
          </button>
        </div>
      )}
    </div>
  );
}
